import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back to Brightside");
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-ivory">
      {/* Hero */}
      <div className="relative hidden w-1/2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzZW5pb3IlMjBjYXJlJTIwY2FyZWdpdmVyJTIwZWxkZXJseSUyMHN1cHBvcnR8ZW58MHx8fHwxNzkwNDA3OTIxfDA&ixlib=rb-4.1.0&q=85"
          alt="Befriender supporting an elderly person"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage">
              <HeartHandshake size={24} />
            </div>
            <span className="font-heading text-xl font-bold">Brightside Befriending Services</span>
          </div>
          <h2 className="max-w-md font-heading text-3xl font-bold leading-tight">
            Bringing warmth and connection to every visit.
          </h2>
          <p className="mt-3 max-w-md text-white/70">
            Manage clients, befrienders, rotas and visit notes — all in one caring place.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage text-white">
              <HeartHandshake size={22} />
            </div>
            <span className="font-heading text-lg font-bold text-ink">Brightside</span>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Sign in</h1>
          <p className="mt-2 text-ink-soft">Welcome back. Please enter your details.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@bsbservices.co.uk"
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-ink outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-ink outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="rounded-lg bg-coral-light px-3 py-2 text-sm text-coral-dark">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-2.5 font-semibold text-white transition-all duration-150 hover:-translate-y-0.5 hover:bg-brand-dark disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Sign in
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ink-muted">
            Brightside Befriending Services · bsbservices.co.uk
          </p>
        </div>
      </div>
    </div>
  );
}
